<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'

export interface AppTab {
  key: string
  label: string
}

type TabSize = 'sm' | 'md'

interface Props {
  modelValue: string
  tabs: AppTab[]
  size?: TabSize
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
})
defineEmits<{ (e: 'update:modelValue', value: string): void }>()

/**
 * `<button>` elements stay raw — they need DOM refs to measure position for
 * the sliding indicator animation. This is the rare-case exception §19 allows
 * for kit citizens wrapping a native interactive control.
 */
const INDICATOR_PADDING_PX_SM = 3
const INDICATOR_PADDING_PX_MD = 5

const btnRefs = ref<HTMLElement[]>([])
const indicator = ref({ left: 0, width: 0 })

const activeIndex = computed(() =>
  props.tabs.findIndex((tab) => tab.key === props.modelValue),
)

function moveIndicator() {
  const activeButton = btnRefs.value[activeIndex.value]
  if (!activeButton) return
  const parent = activeButton.parentElement
  if (!parent) return
  const parentRect = parent.getBoundingClientRect()
  const rect = activeButton.getBoundingClientRect()
  const padding = props.size === 'sm' ? INDICATOR_PADDING_PX_SM : INDICATOR_PADDING_PX_MD
  indicator.value = {
    left: rect.left - parentRect.left + padding,
    width: rect.width - padding * 2,
  }
}

onMounted(() => nextTick(moveIndicator))
watch(
  () => props.modelValue,
  () => nextTick(moveIndicator),
)
</script>

<template>
  <Box flex align="center" :class="['seg-tabs', `seg-tabs--${size}`]">
    <Box
      class="seg-tabs__indicator"
      :style="{ left: `${indicator.left}px`, width: `${indicator.width}px` }"
    />
    <button
      v-for="(tab, i) in tabs"
      :key="tab.key"
      :ref="
        (el) => {
          if (el) btnRefs[i] = el as HTMLElement
        }
      "
      type="button"
      class="seg-tabs__btn"
      :class="{ 'seg-tabs__btn--active': modelValue === tab.key }"
      @click="$emit('update:modelValue', tab.key)"
    >
      <Text is="span" class="seg-tabs__pill">{{ tab.label }}</Text>
    </button>
  </Box>
</template>

<style scoped>
.seg-tabs {
  background: var(--p-surface-100);
  border-radius: 12px;
  flex-shrink: 0;
  position: relative;
  display: inline-flex;
}

/* Animated sliding indicator */
.seg-tabs__indicator {
  position: absolute;
  background: var(--p-surface-0);
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition:
    left 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  z-index: 0;
}

.seg-tabs__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  outline: none;
  position: relative;
  z-index: 1;
}

.seg-tabs__pill {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-weight: 500;
  color: var(--p-text-color);
  white-space: nowrap;
  line-height: 1;
  transition: color 0.2s ease;
}

.seg-tabs__btn--active .seg-tabs__pill {
  color: var(--p-primary-500);
}

.seg-tabs__btn:not(.seg-tabs__btn--active):hover .seg-tabs__pill {
  color: var(--p-text-color);
}

/* ── Size: md (default) ── */
.seg-tabs--md {
  height: 42px;
  border-radius: 12px;
}

.seg-tabs--md .seg-tabs__indicator {
  top: 5px;
  height: 32px;
  border-radius: 10px;
}

.seg-tabs--md .seg-tabs__btn {
  height: 42px;
  padding: 5px;
  border-radius: 10px;
}

.seg-tabs--md .seg-tabs__pill {
  height: 32px;
  padding: 0 20px;
  font-size: 1rem;
}

/* ── Size: sm ── */
.seg-tabs--sm {
  height: 32px;
  border-radius: 9px;
}

.seg-tabs--sm .seg-tabs__indicator {
  top: 3px;
  height: 26px;
  border-radius: 7px;
}

.seg-tabs--sm .seg-tabs__btn {
  height: 32px;
  padding: 3px;
  border-radius: 7px;
}

.seg-tabs--sm .seg-tabs__pill {
  height: 26px;
  padding: 0 12px;
  font-size: 0.8125rem;
}
</style>
